import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Table,
  Checkbox,
  InputNumber,
  message,
} from "antd";
import { useDistributions } from "../../../hooks/bot/useDistributions";

const { TextArea } = Input;

const BulkCollectionModal = ({
  visible,
  distributions,
  mode, // 'bulk-collect' or 'bulk-verify'
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { markCollected, verifyDistribution } = useDistributions();
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  // Filter items based on mode
  const availableItems =
    distributions?.filter((d) => {
      if (mode === "bulk-collect") {
        // Include items that are not yet collected or partially collected
        if (
          d.status === "Pending" ||
          d.status === "Prepared" ||
          d.status === "Ready" ||
          d.status === "Partially Collected"
        ) {
          return true;
        }
        // Include items with partial quantities (Collected status but qty_collected < qty_prepared)
        if (
          d.status === "Collected" &&
          d.quantity_collected < d.quantity_prepared
        ) {
          return true;
        }
        return false;
      } else if (mode === "bulk-verify") {
        // Only show fully collected items for verification
        return (
          d.status === "Collected" &&
          d.quantity_collected >= d.quantity_prepared
        );
      }
      return false;
    }) || [];

  useEffect(() => {
    if (visible) {
      // Pre-select all items by default
      setSelectedItems(
        availableItems.map((item) => ({
          id: item.id,
          quantity: item.quantity_prepared,
          item_name: item.item_name,
          brand_name: item.brand_name,
          size_name: item.size_name,
          quantity_prepared: item.quantity_prepared,
        })),
      );
      form.resetFields();
    }
  }, [visible, distributions]);

  const handleQuantityChange = (itemId, value) => {
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, quantity: value } : item,
      ),
    );
  };

  const handleToggleItem = (item, checked) => {
    if (checked) {
      // Calculate remaining quantity for partially collected items
      const remaining = item.quantity_prepared - (item.quantity_collected || 0);
      setSelectedItems((prev) => [
        ...prev,
        {
          id: item.id,
          quantity: remaining,
          item_name: item.item_name,
          brand_name: item.brand_name,
          size_name: item.size_name,
          quantity_prepared: item.quantity_prepared,
        },
      ]);
    } else {
      setSelectedItems((prev) => prev.filter((i) => i.id !== item.id));
    }
  };

  const handleSubmit = async () => {
    try {
      if (selectedItems.length === 0) {
        message.warning(
          `Please select at least one item to ${mode === "bulk-collect" ? "collect" : "verify"}`,
        );
        return;
      }

      const values = await form.validateFields();
      setLoading(true);

      let results = [];
      if (mode === "bulk-collect") {
        // Mark each selected item as collected SEQUENTIALLY (one by one)
        // This ensures backend properly tracks the progression from Partially → Fully Collected
        for (const item of selectedItems) {
          const result = await markCollected(item.id, {
            quantity_collected: item.quantity,
            collected_by: values.collected_by,
            collected_by_relationship: values.collected_by_relationship,
            id_number: values.id_number,
            notes: values.notes,
          });
          results.push(result);
        }
      } else if (mode === "bulk-verify") {
        // Verify each selected item SEQUENTIALLY
        for (const item of selectedItems) {
          const result = await verifyDistribution(item.id, {
            discrepancy: values.discrepancy || false,
            discrepancy_notes: values.discrepancy_notes || "",
          });
          results.push(result);
        }
      }

      // Check if all succeeded
      const allSucceeded = results.every((result) => result !== null);

      if (allSucceeded) {
        message.success(
          `Successfully ${mode === "bulk-collect" ? "collected" : "verified"} ${selectedItems.length} item(s)`,
        );
        onSuccess();
        onClose();
        form.resetFields();
        setSelectedItems([]);
      } else {
        message.error("Some items failed to update. Please try again.");
      }
    } catch (error) {
      if (error.errorFields) {
        message.error("Please fill in all required fields");
      } else {
        console.error(`Error submitting bulk ${mode}:`, error);
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Select",
      key: "select",
      width: 60,
      render: (_, record) => (
        <Checkbox
          checked={selectedItems.some((item) => item.id === record.id)}
          onChange={(e) => handleToggleItem(record, e.target.checked)}
        />
      ),
    },
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
    },
    {
      title: "Brand",
      dataIndex: "brand_name",
      key: "brand_name",
    },
    {
      title: "Size",
      dataIndex: "size_name",
      key: "size_name",
      render: (text) => text || "-",
    },
    {
      title: "Qty",
      key: "quantity_info",
      align: "center",
      render: (_, record) => {
        const remaining =
          record.quantity_prepared - (record.quantity_collected || 0);
        return (
          <span>
            {record.quantity_collected || 0}/{record.quantity_prepared}
            {remaining > 0 && (
              <span style={{ color: "#ff4d4f" }}> ({remaining} left)</span>
            )}
          </span>
        );
      },
    },
    {
      title: mode === "bulk-collect" ? "Qty to Collect" : "Action",
      key: "quantity_input",
      align: "center",
      width: 120,
      render: (_, record) => {
        if (mode === "bulk-verify") return "-";

        const selectedItem = selectedItems.find(
          (item) => item.id === record.id,
        );
        if (!selectedItem) return "-";

        const remaining =
          record.quantity_prepared - (record.quantity_collected || 0);
        return (
          <InputNumber
            min={1}
            max={remaining}
            value={selectedItem.quantity}
            onChange={(value) => handleQuantityChange(record.id, value)}
            style={{ width: "80px" }}
          />
        );
      },
    },
  ];

  return (
    <Modal
      title="Collect Items"
      open={visible}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={loading}
      width={900}
      okText="Mark as Collected"
      cancelText="Cancel"
    >
      <Form form={form} layout="vertical">
        {/* Items Selection Table */}
        <Form.Item label="Select Items to Collect">
          <Table
            columns={columns}
            dataSource={availableItems}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ y: 300 }}
          />
          <div style={{ marginTop: "8px", color: "#888" }}>
            Selected: {selectedItems.length} item(s)
          </div>
        </Form.Item>

        {/* Collector Information (only for collection mode) */}
        {mode === "bulk-collect" && (
          <>
            <Form.Item
              name="collected_by"
              label="Collected By (Name)"
              rules={[
                { required: true, message: "Please enter collector name" },
              ]}
            >
              <Input placeholder="Full name of person collecting" />
            </Form.Item>

            <Form.Item
              name="collected_by_relationship"
              label="Relationship (Optional)"
            >
              <Input placeholder="e.g., Parent, Guardian, Student" />
            </Form.Item>

            <Form.Item name="id_number" label="ID Number (Optional)">
              <Input placeholder="National ID or other ID" />
            </Form.Item>

            <Form.Item name="notes" label="Notes">
              <TextArea rows={2} placeholder="Any additional notes..." />
            </Form.Item>
          </>
        )}

        {/* Verification Information (only for verification mode) */}
        {mode === "bulk-verify" && (
          <>
            <Form.Item name="discrepancy" valuePropName="checked">
              <Checkbox>Report Discrepancy for all selected items</Checkbox>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.discrepancy !== curr.discrepancy
              }
            >
              {({ getFieldValue }) =>
                getFieldValue("discrepancy") ? (
                  <Form.Item
                    name="discrepancy_notes"
                    label="Discrepancy Details"
                    rules={[
                      { required: true, message: "Please describe the issue" },
                    ]}
                  >
                    <TextArea
                      rows={3}
                      placeholder="Describe the problem for all items..."
                    />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default BulkCollectionModal;
